import { useColorImage } from "../../data/useColorImages";
// AddColorModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Col,
  Row,
  Image,
  FloatingLabel
} from "react-bootstrap";
import colorApi from "../../data/api/colorApi";
import { useDispatch, useSelector } from "react-redux";
import { useLanguage } from "../helpers/LanguageContext";

const AddColorModal = ({ show, onHide, colorToEdit }) => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const isEdit = Boolean(colorToEdit);
  const fullColor = useColorImage(colorToEdit, show && isEdit);

  const [colorName, setColorName] = useState("");
  const [colorType, setColorType] = useState("PAL");
  const [colorRotable, setColorRotable] = useState(true);
  const [colorActive, setColorActive] = useState(true);
  const [colorPrice, setColorPrice] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [colorDimension, setColorDimension] = useState("[2780,2050,18]");
  const [groupName, setColorGroupName] = useState("");
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitDimension, setSplitDimension] = useState("");
  const millings = useSelector((state) => state.millings || []);
  const [millingSearch, setMillingSearch] = useState("");
  const [selectedMillings, setSelectedMillings] = useState([]);
  const [showAllMillings, setShowAllMillings] = useState(false);

  function defaultSplitFromDimension(dimStr) {
    try {
      const [H, W, T] = JSON.parse(dimStr || "[0,0,0]");
      if (!isFinite(H) || !isFinite(W) || !isFinite(T) || H <= 0 || W <= 0)
        return "";
      return `[${H},${Math.round(W / 2)},${T}]`;
    } catch {
      return "";
    }
  }

  useEffect(() => {
    if (isEdit) {
      setColorName(colorToEdit.name);
      setColorType(colorToEdit.materialType);
      setColorRotable(colorToEdit.rotable);
      setColorActive(colorToEdit.active);
      setColorPrice(colorToEdit.price);
      setColorDimension(colorToEdit.dimension);
      setSelectedFile(null);
      setColorGroupName(colorToEdit.groupName);

      if (
        colorToEdit.splitDimension &&
        String(colorToEdit.splitDimension).trim()
      ) {
        setSplitEnabled(true);
        setSplitDimension(colorToEdit.splitDimension);
      } else {
        setSplitEnabled(false);
        setSplitDimension("");
      }

      setPreviewImage(null);

      setSelectedMillings(
        colorToEdit.millingAssignments
          ? colorToEdit.millingAssignments.split(",").map((x) => Number(x))
          : []
      );
    } else {
      setColorName("");
      setColorType("PAL");
      setColorRotable(true);
      setColorActive(true);
      setColorPrice(0);
      setSelectedFile(null);
      setPreviewImage(null);
      setColorDimension("[2780,2050,18]");
      setColorGroupName("");
      setSplitEnabled(false);
      setSplitDimension("");
      setSelectedMillings([]);
    }
  }, [isEdit, colorToEdit]);

  useEffect(() => {
    if (!show) {
      setPreviewImage(null);
      setFullScreenImage(null);
      setSelectedFile(null);
    } else if (isEdit && !selectedFile) {
      setPreviewImage(fullColor?.imageData
        ? `data:${colorToEdit.imageContentType || "image/jpeg"};base64,${fullColor.imageData}` : null);
    }
  }, [show, isEdit, selectedFile, fullColor?.imageData, colorToEdit?.imageContentType]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setColorName(file.name.split(".")[0]);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  function cropBase64Center(base64Str, cropW = 400, cropH = 400) {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.src = base64Str;
      img.onload = () => {
        const sw = Math.min(cropW, img.width);
        const sh = Math.min(cropH, img.height);
        const sx = Math.floor((img.width - sw) / 2);
        const sy = Math.floor((img.height - sh) / 2);
        const canvas = document.createElement("canvas");
        canvas.width = sw;
        canvas.height = sh;
        canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isEdit && !previewImage) {
      alert(t("colorNoImageAlert"));
      return;
    }

    const colorData = {
      imageId: 0,
      active: colorActive,
      dimension: colorDimension,
      materialType: colorType,
      name: colorName,
      rotable: colorRotable,
      price: colorPrice,
      groupName
    };

    try {
      const replaceImage = !isEdit || !!selectedFile;
      const reduced = replaceImage ? await cropBase64Center(previewImage, 400, 400) : null;
      const payload = {
        ...colorData,
        ...(replaceImage ? {
          imageContentType: previewImage.split(";")[0].split(":")[1],
          imageData: previewImage.split(",")[1],
          imageDataReduced: reduced.split(",")[1]
        } : {}),
        splitDimension: splitEnabled ? splitDimension : "",
        millingAssignments: selectedMillings.join(",")
      };

      if (isEdit) {
        await dispatch(
          colorApi.updateColorApi(colorToEdit.colorId, payload, null)
        );
        setSelectedMillings(
          colorToEdit.millingAssignments
            ? colorToEdit.millingAssignments.split(",").map((x) => Number(x))
            : []
        );
      } else {
        await dispatch(colorApi.createColorApi(payload, null));
        setSelectedMillings([]);
      }
      onHide();
    } catch (error) {
      console.error("Error saving color:", error);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? t("editColorTitle") : t("addColorTitle")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {fullColor?.imageError && <p role="alert">A teljes k?p bet?lt?se sikertelen. A metaadatok tov?bbra is menthet?k.</p>}
            <Row>
              <Col md={6}>
                <FloatingLabel
                  controlId="floatingColorName"
                  label={t("colorNameLabel")}
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder={t("colorNamePlaceholder")}
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                    required
                  />
                </FloatingLabel>
                <Row className="g-3">
                  <Col>
                    <FloatingLabel
                      controlId="colorType"
                      label={t("colorTypeLabel")}
                    >
                      <Form.Select
                        value={colorType}
                        onChange={(e) => {
                          const v = e.target.value;
                          setColorType(v);
                          if (v === "MDF") setColorDimension("NaN");
                          else if (v === "Gizir")
                            setColorDimension("[2780,1200,18]");
                          else setColorDimension("[2780,2050,18]");
                          setSplitDimension((old) =>
                            splitEnabled
                              ? defaultSplitFromDimension(
                                  v === "MDF"
                                    ? "NaN"
                                    : v === "Gizir"
                                      ? "[2780,1200,18]"
                                      : "[2780,2050,18]"
                                )
                              : old
                          );
                        }}
                      >
                        <option value="PAL">PAL</option>
                        <option value="MDF">MDF</option>
                        <option value="Gizir">Gizir</option>
                        <option value="PFL">PFL</option>
                        <option value="Munkalap">Munkalap</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel
                      controlId="groupName"
                      label={t("colorGroupLabel")}
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder={t("colorGroupPlaceholder")}
                        value={groupName}
                        onChange={(e) => setColorGroupName(e.target.value)}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel
                      controlId="dimension"
                      label={t("colorDimensionLabel")}
                    >
                      <Form.Control value={colorDimension} disabled />
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row className="g-3 mt-3">
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      id="split-enabled"
                      label={t("colorSplitSwitch")}
                      checked={splitEnabled}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSplitEnabled(on);
                        if (on && !splitDimension) {
                          setSplitDimension(
                            defaultSplitFromDimension(colorDimension) ||
                              "[2780,1025,18]"
                          );
                        }
                      }}
                    />
                  </Col>
                  <Col md={8}>
                    {splitEnabled && (
                      <Row className="g-2">
                        <Col>
                          <Form.Control
                            type="text"
                            value={splitDimension}
                            onChange={(e) => setSplitDimension(e.target.value)}
                            placeholder={t("colorSplitPlaceholder")}
                          />
                        </Col>
                        <Col xs="auto">
                          <Button
                            variant="outline-secondary"
                            onClick={() =>
                              setSplitDimension(
                                defaultSplitFromDimension(colorDimension) ||
                                  "[2780,1025,18]"
                              )
                            }
                          >
                            {t("colorSplitDefault")}
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>

                <Row className="g-3 mt-3">
                  <Col>
                    <FloatingLabel
                      controlId="rotable"
                      label={t("colorRotableLabel")}
                    >
                      <Form.Select
                        value={String(colorRotable)}
                        onChange={(e) =>
                          setColorRotable(e.target.value === "true")
                        }
                      >
                        <option value="true">{t("colorRotableTrue")}</option>
                        <option value="false">{t("colorRotableFalse")}</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel
                      controlId="active"
                      label={t("colorActiveLabel")}
                    >
                      <Form.Select
                        value={String(colorActive)}
                        onChange={(e) =>
                          setColorActive(e.target.value === "true")
                        }
                      >
                        <option value="true">{t("colorActiveTrue")}</option>
                        <option value="false">{t("colorActiveFalse")}</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col>
                    <FloatingLabel
                      controlId="price"
                      label={t("colorPriceLabel")}
                    >
                      <Form.Control
                        type="number"
                        placeholder={t("colorPriceLabel")}
                        value={colorPrice}
                        onChange={(e) => setColorPrice(e.target.value)}
                        required
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                <FloatingLabel
                  className="mt-3"
                  controlId="imageUpload"
                  label={t("colorImageLabel")}
                >
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </FloatingLabel>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{t("colorMillingTitle")}</h6>
                    <Button
                      size="sm"
                      variant={showAllMillings ? "outline-primary" : "primary"}
                      onClick={() => setShowAllMillings((prev) => !prev)}
                    >
                      {showAllMillings
                        ? t("colorMillingShowCompatible")
                        : t("colorMillingShowAll")}
                    </Button>
                  </div>

                  <Form.Control
                    type="text"
                    placeholder={t("colorMillingSearch")}
                    className="mb-2"
                    value={millingSearch}
                    onChange={(e) => setMillingSearch(e.target.value)}
                  />

                  <div className="mb-2 d-flex flex-wrap gap-1">
                    {selectedMillings.map((id) => {
                      const m = millings.find((x) => x.millingId === id);
                      if (!m) return null;
                      return (
                        <span
                          key={id}
                          style={{
                            background: "#0d6efd",
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                        >
                          {m.name}
                          <span
                            style={{ cursor: "pointer", fontWeight: "bold" }}
                            onClick={() =>
                              setSelectedMillings((prev) =>
                                prev.filter((x) => x !== id)
                              )
                            }
                          >
                            ×
                          </span>
                        </span>
                      );
                    })}
                  </div>

                  <div
                    className="milling-scroll"
                    style={{
                      maxHeight: "220px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      background: "#fff"
                    }}
                  >
                    {millings
                      .filter((m) => {
                        if (!showAllMillings && m.materialType !== colorType)
                          return false;
                        if (!millingSearch) return true;
                        return m.name
                          ?.toLowerCase()
                          .includes(millingSearch.toLowerCase());
                      })
                      .map((m) => {
                        const selected = selectedMillings.includes(m.millingId);
                        const compatible = m.materialType === colorType;
                        return (
                          <div
                            key={m.millingId}
                            onClick={() => {
                              if (selected) {
                                setSelectedMillings((prev) =>
                                  prev.filter((id) => id !== m.millingId)
                                );
                              } else {
                                setSelectedMillings((prev) => [
                                  ...prev,
                                  m.millingId
                                ]);
                              }
                            }}
                            style={{
                              padding: "8px 10px",
                              cursor: "pointer",
                              background: selected ? "#e7f1ff" : "transparent",
                              borderBottom: "1px solid #f1f1f1",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              opacity: compatible ? 1 : 0.5,
                              transition: "all 0.15s"
                            }}
                          >
                            <div>
                              <div>{m.name}</div>
                              {!compatible && (
                                <small style={{ color: "#dc3545" }}>
                                  {t("colorMillingIncompatible")}
                                </small>
                              )}
                            </div>
                            {selected && (
                              <i className="bi bi-check-lg text-primary"></i>
                            )}
                          </div>
                        );
                      })}
                  </div>

                  {millings.length === 0 && (
                    <div className="text-muted mt-2">
                      {t("colorMillingNone")}
                    </div>
                  )}
                </div>

                {isEdit && !selectedFile && previewImage && (
                  <Form.Text className="text-muted">
                    {t("colorImageNote")}
                  </Form.Text>
                )}
              </Col>

              <Col
                md={6}
                className="d-flex justify-content-center align-items-center"
              >
                {previewImage ? (
                  <Image
                    src={previewImage}
                    alt={t("colorPreviewTitle")}
                    fluid
                    style={{
                      width: "100%",
                      height: "50vh",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      overflow: "hidden",
                      cursor: "pointer"
                    }}
                    onClick={() => setFullScreenImage(previewImage)}
                  />
                ) : (
                  <div className="text-muted">{t("colorNoImage")}</div>
                )}
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
              {t("cancel")}
            </Button>
            <Button variant="primary" type="submit">
              {isEdit ? t("colorSaveBtn") : t("colorAddBtn")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!fullScreenImage}
        onHide={() => setFullScreenImage(null)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("colorPreviewTitle")} - {colorName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <Image src={fullScreenImage} fluid />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddColorModal;
